import React, { useEffect, useState } from "react";
import { getTasks } from "@/utils/pasUtils";
import { useUserInfo } from "@/hooks/useUserInfo.ts";
import { WelcomeHeader } from "@/components/WelcomeHeader";
import { Button } from "@/components/ui/button.tsx";
import { useLanguage } from "@/contexts/LanguageContext.tsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { AlertCircle, Calendar, Clock, FileText } from "lucide-react";
import { StatusBadge } from "@/components/ui/status-badge.tsx";

function Tasks() {
  const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
  const VITE_PASSWORD = import.meta.env.VITE_PASSWORD

  const { t } = useLanguage();

  const { greeting, fullName, email, role, profilePictureUrl } = useUserInfo();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchTasks() {
      try {
        setLoading(true);
        setError("");

        const records = await getTasks(
          VITE_USERNAMET,
          VITE_PASSWORD,
          email
        );

        setTasks(Array.isArray(records) ? records : []);
      } catch (err) {
        setError(err.message || "Failed to fetch attendance");
      } finally {
        setLoading(false);
      }
    }

    fetchTasks();
  }, []);

  return (
    <div className="space-y-6 sm:space-y-8 fade-in">
      {/* Welcome Section */}
      <WelcomeHeader />

      {/* Task Records */}
      <section className="surface pb-2">
        <div className="surface-head">
          <h2 className="surface-title">
            <Clock className="h-5 w-5 text-primary" />
            سجل المهام
          </h2>
        </div>

        {loading && (
          <div className="rows animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 py-5">
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-24 rounded bg-muted"></div>
                  <div className="h-4 w-2/3 rounded bg-muted"></div>
                </div>
                <div className="h-6 w-14 rounded-full bg-muted"></div>
              </div>
            ))}
          </div>
        )}
        {error && (
          <div className="text-center py-8">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
            <p className="text-destructive font-medium">{error}</p>
          </div>
        )}

        <div className="rows">
          {!loading &&
            !error &&
            tasks &&
            Array.isArray(tasks) &&
            tasks.map((task, index) => (
              <div key={index} className="flex items-start gap-4 py-4">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-muted-foreground mb-1">
                    #{task.task_id}
                  </p>
                  <h4 className="font-bold leading-snug break-words">
                    {task.task_subject}
                  </h4>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {t("tasks.start_date")} {task.exp_start_date}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {t("tasks.end_date")} {task.exp_start_date}
                    </span>
                  </div>
                </div>
                <StatusBadge status="in_progress" className="text-xs shrink-0">
                  {t("tasks.overdue")}
                </StatusBadge>
              </div>
            ))}
        </div>

        {!loading && !error && tasks && tasks.length === 0 && (
          <div className="text-center py-12">
            <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="h-7 w-7 text-muted-foreground" />
            </div>
            <h3 className="font-bold mb-1">{t("tasks.no_tasks")}</h3>
            <p className="text-muted-foreground text-sm">
              {t("tasks.upcoming_tasks")}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

export default Tasks;
